import { AccountInfo } from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { createContext, ReactNode, useCallback, useContext } from 'react';
import { loginRequest } from '../config/authConfig';

/**
 * Microsoft Entra ID Authentication Context
 *
 * This handles user authentication with Microsoft accounts.
 * Separate from Intervals.icu integration (which is in AuthContext).
 */

interface EntraAuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: (forceRefresh?: boolean) => Promise<string | null>;
}

const EntraAuthContext = createContext<EntraAuthContextType | undefined>(undefined);

export const EntraAuthProvider = ({ children }: { children: ReactNode }) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || null);
  const isAuthenticated = useIsAuthenticated();

  /**
   * Initiate login flow with popup
   */
  const login = async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Entra ID login failed:', error);
      throw error;
    }
  };

  /**
   * Logout user
   */
  const logout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  /**
   * Get ID token for API authentication
   * For SPAs, the ID token is used to authenticate with the backend
   * Automatically refreshes expired tokens or falls back to interactive login
   */
  const getAccessToken = useCallback(
    async (forceRefresh: boolean = false): Promise<string | null> => {
      // Get the current account from MSAL instance to avoid stale closure issues
      const currentAccounts = instance.getAllAccounts();
      const currentAccount = currentAccounts[0];

      if (!currentAccount) {
        console.warn('[EntraAuth] No account available for token acquisition');
        return null;
      }

      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: currentAccount,
          forceRefresh, // Force refresh if explicitly requested (e.g., after 401)
        });
        // Return the ID token for backend validation
        return response.idToken;
      } catch (error: any) {
        console.warn('[EntraAuth] Silent token acquisition failed:', error);

        // Check if error is due to interaction required or token expired
        const needsInteraction =
          error?.errorCode === 'interaction_required' ||
          error?.errorCode === 'login_required' ||
          error?.errorCode === 'consent_required' ||
          error?.name === 'InteractionRequiredAuthError';

        if (needsInteraction) {
          // Fall back to interactive popup if silent acquisition fails
          try {
            const response = await instance.acquireTokenPopup(loginRequest);
            return response.idToken;
          } catch (interactiveError) {
            console.error('[EntraAuth] Interactive token acquisition failed:', interactiveError);
            // Dispatch auth error event to trigger logout and redirect
            window.dispatchEvent(new CustomEvent('auth-error', { detail: 'token_refresh_failed' }));
            return null;
          }
        } else {
          // Other errors (network, etc.) - dispatch auth error
          console.error('[EntraAuth] Token acquisition failed:', error);
          window.dispatchEvent(
            new CustomEvent('auth-error', { detail: 'token_acquisition_failed' })
          );
          return null;
        }
      }
    },
    [instance]
  );

  return (
    <EntraAuthContext.Provider
      value={{
        isAuthenticated,
        user: account,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </EntraAuthContext.Provider>
  );
};

/**
 * Custom hook to use Entra ID authentication
 */
export const useEntraAuth = () => {
  const context = useContext(EntraAuthContext);
  if (!context) {
    throw new Error('useEntraAuth must be used within an EntraAuthProvider');
  }
  return context;
};
